import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from "../components/ui/button";
import { BlockLink, MediaSlotView } from "../render/context";
import { resolveSlotMedia } from "../render/slot-media";
import { PAGE_GUTTER, Section } from "./_section";
/**
 * The centered opening itself, without block chrome — so a component harness
 * renders exactly what a page renders.
 *
 * Everything is centered and stacked: the title, the description beneath it, the
 * call to action, then the picture. There is no alignment choice and no layout
 * choice, which is what separates this from the plain Hero.
 *
 * The title is a real `<h1>`: this is what opens the page, and a page-opening
 * hero whose title is ordinary text leaves the page with nothing to navigate by.
 *
 * The call to action goes through the shared {@link BlockLink}. That is where
 * internal-versus-off-site is decided for every block on the site, so this
 * component states no rule of its own about it and cannot drift from one.
 *
 * Sizing is in `cqw`, not `vw`, so the CMS canvas device frames reflow
 * truthfully; the breakpoints are 901/1101 because the design prototype's
 * `width <= 900px` and `<= 1100px` include those widths and Tailwind's
 * `@min-[N]` excludes them.
 */
export function HeroMediaCenteredView({ data, }) {
    const { title, description, cta, media } = data;
    return (_jsxs("div", { className: "mx-auto flex w-full max-w-[1300px] flex-col items-center gap-10 text-center", children: [_jsxs("div", { className: "flex w-full max-w-[clamp(200px,100%,900px)] flex-col items-center gap-4", children: [_jsx("h1", { "data-hero-media-centered-title": true, className: "text-balance font-display text-[clamp(52px,13.5cqw,90px)] font-light leading-[1.04] tracking-[-0.02em] text-brand-blue @min-[901px]:text-[clamp(64px,9cqw,120px)]", children: title }), description ? (_jsx("p", { "data-hero-media-centered-description": true, className: "max-w-[680px] text-pretty font-body text-[18px] font-light leading-[1.55] text-foreground @min-[901px]:text-[20px] @min-[1101px]:max-w-none @min-[1101px]:whitespace-nowrap", children: description })) : null, cta?.label && cta?.href ? (_jsx("span", { "data-hero-media-centered-cta": true, className: "mt-4 inline-flex", children: _jsx(Button, { asChild: true, className: "px-7 font-medium", children: _jsx(BlockLink, { href: cta.href, children: cta.label }) }) })) : null] }), _jsx("div", { "data-hero-media-centered-media": true, className: "h-[220px] w-[300px] max-w-full overflow-hidden rounded-[15px]", children: _jsx(MediaSlotView, { image: media, ratio: "15 / 11", label: "Hero media", className: "size-full object-cover", emptyRender: null }) })] }));
}
export function HeroMediaCenteredBlock({ data, }) {
    return (_jsx(Section, { tone: "default", className: PAGE_GUTTER, children: _jsx(HeroMediaCenteredView, { data: data }) }));
}
export function heroMediaCenteredFromBlock(block, resolve) {
    const scalars = block.scalars ?? {};
    return {
        title: scalars.title ?? "",
        description: scalars.description ?? "",
        cta: { label: scalars.ctaLabel ?? "", href: scalars.ctaHref ?? "" },
        // One declared target, so this resolves to a single framing every screen
        // sees.
        media: resolveSlotMedia(block, "media", resolve),
    };
}
export const heroMediaCenteredBlockEntry = {
    Component: HeroMediaCenteredBlock,
    fromBlock: heroMediaCenteredFromBlock,
};
//# sourceMappingURL=hero-media-centered.js.map